import type { JsonSchemaType, MetadataFieldProperty } from '@gmn-portal/backend';
import jsonSchemaToZod from 'json-schema-to-zod';
import { DateTime } from 'luxon';
import { z } from 'zod';

function evaluateZodSchemaExpression(schemaExpression: string): unknown {
  // `json-schema-to-zod` returns a string expression that references `z.*`.
  // In production builds, bundlers/minifiers may rename the imported `z` binding,
  // which breaks `eval()` because the expression still references `z`.
  // Using `Function('z', ...)` pins the identifier name via an explicit parameter.
  return Function('z', `"use strict"; return (${schemaExpression});`)(z);
}

/**
 * Parses metadata values, converting ISO date strings to Date objects
 */
export function parseMetadata(metadata?: Record<string, unknown>): Record<string, unknown> {
  if (!metadata) return {};

  const entries = Object.entries(metadata).sort((a, b) => a[0].localeCompare(b[0]));

  return Object.fromEntries(
    entries.map(([key, value]) => {
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value)) {
        return [key, DateTime.fromISO(value).toJSDate()];
      }
      return [key, value];
    })
  );
}

/**
 * Checks if a metadata schema has any fields that are customer editable
 * @param schemas One or more JSON schemas to check for editable fields
 * @returns boolean indicating if any schema has editable fields
 */
export type FieldPermission = {
  read: boolean;
  update: boolean;
};

const legacyCustomerAccessToPermission = (
  access: MetadataFieldProperty['customerAccess'] | undefined
): FieldPermission => {
  if (access === 'read-and-write') {
    return { read: true, update: true };
  }
  if (access === 'read') {
    return { read: true, update: false };
  }
  return { read: false, update: false };
};

export const getFieldPermission = (
  field: Partial<MetadataFieldProperty> | undefined,
  groupKey: string | null
): FieldPermission => {
  if (!field || !groupKey) {
    return { read: false, update: false };
  }

  if (groupKey === 'gmn-admin') {
    return { read: true, update: true };
  }

  if (field.permissions && typeof field.permissions === 'object') {
    const record = field.permissions as Record<string, { read: boolean; update: boolean }>;
    const entry = record[groupKey];
    if (entry) {
      return {
        read: Boolean(entry.read),
        update: Boolean(entry.update),
      };
    }
  }

  if (field.customerAccess) {
    return legacyCustomerAccessToPermission(field.customerAccess);
  }

  return { read: false, update: false };
};

export function hasEditableMetadata(
  groupKey: string | null,
  ...schemas: (JsonSchemaType | undefined)[]
): boolean {
  return schemas.some(
    (schema) =>
      schema?.properties &&
      Object.values(schema.properties).some((prop) => getFieldPermission(prop, groupKey).update)
  );
}

/**
 * Transforms a zod schema string to use coercion for numbers
 * This allows form inputs (which return strings) to be validated as numbers
 */
function transformZodSchemaForFormInput(zodSchemaString: string): string {
  return (
    zodSchemaString
      // Replace z.number() with z.coerce.number() for form input compatibility
      .replace(/z\.number\(\)/g, 'z.coerce.number()')
      // Replace datetime strings with Date types
      .replace(/z\.string\(\)\.datetime\({ offset: true }\)/g, 'z.coerce.date()')
  );
}

const contactJsonSchema = {
  type: 'object' as const,
  properties: {
    name: { type: 'string' },
    phone1: { type: 'string' },
    phone2: { type: 'string' },
    email: { type: 'string' },
    fallbackContact: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        phone1: { type: 'string' },
        phone2: { type: 'string' },
        email: { type: 'string' },
      },
      required: [],
    },
  },
  required: [],
};

const positionsJsonSchema = {
  type: 'array' as const,
  items: {
    type: 'object',
    properties: {
      id: { type: 'string' },
      label: { type: 'string' },
      latitude: { type: 'string' },
      longitude: { type: 'string' },
      status: { type: 'boolean' },
    },
    // Only id is strictly required - label can be empty string
    required: ['id'],
  },
};

const statusRangeJsonSchema = {
  type: 'object' as const,
  properties: {
    id: { type: 'string' },
    startDate: { type: 'string' },
    endDate: { type: 'string' },
    statusDuring: { type: 'string', enum: ['red', 'yellow', 'green'] },
    fallbackAfter: { type: 'string', enum: ['red', 'yellow', 'green'] },
  },
  required: ['id', 'startDate', 'endDate', 'statusDuring'],
};

const statusJsonSchema = {
  type: 'object' as const,
  properties: {
    mode: { type: 'string', enum: ['manual', 'scheduled'] },
    manualStatus: { type: 'string', enum: ['red', 'yellow', 'green'] },
    ranges: { type: 'array', items: statusRangeJsonSchema },
    defaultStatus: { type: 'string', enum: ['red', 'yellow', 'green'] },
  },
  required: ['mode', 'defaultStatus'],
};

function preprocessMetadataSchema(schema: JsonSchemaType) {
  const processedProperties: Record<string, unknown> = {};

  for (const [key, prop] of Object.entries(schema.properties)) {
    if (prop.type === 'contact') {
      processedProperties[key] = {
        ...prop,
        ...contactJsonSchema,
      };
    } else if (prop.type === 'positions') {
      processedProperties[key] = {
        ...prop,
        ...positionsJsonSchema,
      };
    } else if (prop.type === 'status') {
      processedProperties[key] = {
        ...prop,
        ...statusJsonSchema,
      };
    } else {
      processedProperties[key] = prop;
    }
  }

  return {
    ...schema,
    properties: processedProperties,
  };
}

// Status field validation error types - used for localization mapping
export type StatusValidationErrorType = 'incomplete' | 'endBeforeStart' | 'overlap';

interface StatusValidationResult {
  valid: boolean;
  errorType?: StatusValidationErrorType;
}

/**
 * Validates a status field value for:
 * 1. Incomplete ranges (missing startDate, endDate, or statusDuring)
 * 2. End date before start date
 * 3. Overlapping ranges
 * Returns validation result with error type for localization
 */
function validateStatusFieldValue(value: unknown): StatusValidationResult {
  if (!value || typeof value !== 'object') return { valid: true };

  const status = value as {
    mode?: string;
    ranges?: Array<{
      startDate?: string;
      endDate?: string;
      statusDuring?: string;
    }>;
  };

  // Only validate ranges in scheduled mode
  if (status.mode !== 'scheduled') return { valid: true };

  // If no ranges, it's valid
  if (!status.ranges || status.ranges.length === 0) return { valid: true };

  // Check for incomplete ranges
  const hasIncompleteRange = status.ranges.some(
    (range) => !range.startDate || !range.endDate || !range.statusDuring
  );
  if (hasIncompleteRange) {
    return { valid: false, errorType: 'incomplete' };
  }

  // Check for end date before start date
  for (const range of status.ranges) {
    if (range.startDate && range.endDate) {
      const start = new Date(range.startDate);
      const end = new Date(range.endDate);
      if (end < start) {
        return { valid: false, errorType: 'endBeforeStart' };
      }
    }
  }

  // Check for overlapping ranges
  for (let i = 0; i < status.ranges.length; i++) {
    const rangeA = status.ranges[i];
    if (!rangeA.startDate || !rangeA.endDate) continue;

    for (let j = i + 1; j < status.ranges.length; j++) {
      const rangeB = status.ranges[j];
      if (!rangeB.startDate || !rangeB.endDate) continue;

      const startA = new Date(rangeA.startDate);
      const endA = new Date(rangeA.endDate);
      const startB = new Date(rangeB.startDate);
      const endB = new Date(rangeB.endDate);

      // Two ranges overlap if: startA <= endB && startB <= endA
      if (startA <= endB && startB <= endA) {
        return { valid: false, errorType: 'overlap' };
      }
    }
  }

  return { valid: true };
}

// Error type to localization key mapping
const statusErrorMessages: Record<StatusValidationErrorType, string> = {
  incomplete: 'STATUS_INCOMPLETE_RANGES',
  endBeforeStart: 'STATUS_END_BEFORE_START',
  overlap: 'STATUS_RANGES_OVERLAP',
};

/**
 * Adds refinements to a zod schema for status field validation
 * This ensures incomplete/invalid ranges in scheduled mode will fail validation on form submit
 */
function addStatusFieldRefinements(
  baseSchema: z.ZodObject<z.ZodRawShape>,
  metadataSchema: JsonSchemaType
): z.ZodObject<z.ZodRawShape> {
  // Find all status fields in the schema
  const statusFields = Object.entries(metadataSchema.properties || {})
    .filter(([, prop]) => prop.type === 'status')
    .map(([key]) => key);

  if (statusFields.length === 0) {
    return baseSchema;
  }

  // Add a superRefine to validate all status fields
  return baseSchema.superRefine((data, ctx) => {
    for (const fieldKey of statusFields) {
      const fieldValue = data[fieldKey];
      const result = validateStatusFieldValue(fieldValue);
      if (!result.valid && result.errorType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          // Use error code for localization mapping in the component
          message: statusErrorMessages[result.errorType],
          path: [fieldKey],
        });
      }
    }
  }) as unknown as z.ZodObject<z.ZodRawShape>;
}

/**
 * Filters a metadata schema based on customer access permissions and converts it to a Zod schema
 * @param metadataSchema The original JSON schema
 * @param isGmnUser Whether the current user is a GMN user
 * @returns A Zod schema with filtered fields based on customer access
 */
export function createMetadataZodSchema(
  metadataSchema: JsonSchemaType | undefined,
  groupKey: string | null
): z.ZodObject<z.ZodRawShape> {
  if (!metadataSchema) {
    return z.object({});
  }

  const preprocessedSchema = preprocessMetadataSchema(metadataSchema);
  const properties = preprocessedSchema.properties as Record<
    string,
    Partial<MetadataFieldProperty>
  >;
  const required = preprocessedSchema.required as string[];
  const propertyOrder = preprocessedSchema.propertyOrder as string[];

  // Filter out fields without read permission
  const readableKeys = new Set(
    Object.entries(properties)
      .filter(([, prop]) => getFieldPermission(prop, groupKey).read)
      .map(([key]) => key)
  );

  const filteredSchema = {
    ...preprocessedSchema,
    properties: Object.fromEntries(
      Object.entries(properties).filter(([key]) => readableKeys.has(key))
    ),
    required: required.filter((field) => readableKeys.has(field)),
    propertyOrder: propertyOrder.filter((field) => readableKeys.has(field)),
  };

  const zodSchemaString = jsonSchemaToZod(filteredSchema);
  const transformedSchema = transformZodSchemaForFormInput(zodSchemaString);
  const baseSchema = evaluateZodSchemaExpression(transformedSchema) as z.ZodObject<z.ZodRawShape>;

  // Also add status field refinements for filtered schema
  // Use the original metadataSchema for field detection but filtered for access
  const filteredMetadataSchema: JsonSchemaType = {
    ...metadataSchema,
    properties: Object.fromEntries(
      Object.entries(metadataSchema.properties || {}).filter(([key]) => readableKeys.has(key))
    ),
  };

  return addStatusFieldRefinements(baseSchema, filteredMetadataSchema);
}

export const createEmptyMetadataSchema = (): JsonSchemaType => ({
  type: 'object',
  properties: {},
  required: [],
  propertyOrder: [],
});

/**
 * Merges two metadata schemas. Category schema fields come first, then object-specific fields.
 * Object fields can add new fields but not override category fields.
 */
export function mergeMetadataSchemas(
  categorySchema: JsonSchemaType | null | undefined,
  objectSchema: JsonSchemaType | null | undefined
): JsonSchemaType {
  const emptySchema = createEmptyMetadataSchema();

  if (!categorySchema && !objectSchema) {
    return emptySchema;
  }

  if (!categorySchema) {
    return objectSchema || emptySchema;
  }

  if (!objectSchema) {
    return categorySchema;
  }

  // Merge properties: category fields first, then object fields (object can add new fields but not override)
  const mergedProperties: JsonSchemaType['properties'] = {
    ...categorySchema.properties,
  };

  // Add object-specific fields that don't exist in category
  for (const [key, value] of Object.entries(objectSchema.properties || {})) {
    if (!mergedProperties[key]) {
      mergedProperties[key] = value;
    }
  }

  // Merge required: combine both arrays, removing duplicates
  const mergedRequired = [
    ...new Set([...(categorySchema.required || []), ...(objectSchema.required || [])]),
  ];

  // Merge propertyOrder: category order first, then object-specific fields
  const categoryOrder = categorySchema.propertyOrder || [];
  const objectOrder = objectSchema.propertyOrder || [];
  const objectOnlyOrder = objectOrder.filter((key) => !categoryOrder.includes(key));
  const mergedPropertyOrder = [...categoryOrder, ...objectOnlyOrder];

  return {
    type: 'object',
    properties: mergedProperties,
    required: mergedRequired,
    propertyOrder: mergedPropertyOrder,
  };
}

function sanitizeContactValue(val: unknown): Record<string, unknown> | undefined {
  if (!val || typeof val !== 'object') return undefined;

  const contact = val as Record<string, unknown>;
  const result: Record<string, unknown> = {};

  for (const field of ['name', 'phone1', 'phone2', 'email']) {
    const fieldVal = contact[field];
    if (typeof fieldVal === 'string' && fieldVal.trim() !== '') {
      result[field] = fieldVal.trim();
    }
  }

  if (contact.fallbackContact && typeof contact.fallbackContact === 'object') {
    const fallback = contact.fallbackContact as Record<string, unknown>;
    const sanitizedFallback: Record<string, unknown> = {};

    for (const field of ['name', 'phone1', 'phone2', 'email']) {
      const fieldVal = fallback[field];
      if (typeof fieldVal === 'string' && fieldVal.trim() !== '') {
        sanitizedFallback[field] = fieldVal.trim();
      }
    }

    if (Object.keys(sanitizedFallback).length > 0) {
      result.fallbackContact = sanitizedFallback;
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

interface PositionEntry {
  id?: string;
  label?: string;
  latitude?: string;
  longitude?: string;
  status?: boolean;
}

function sanitizePositionsValue(val: unknown): PositionEntry[] | undefined {
  if (!val || !Array.isArray(val)) return undefined;

  const result: PositionEntry[] = [];

  for (const item of val) {
    if (!item || typeof item !== 'object') continue;

    const position = item as PositionEntry;

    // id is required - skip positions without valid id
    if (typeof position.id !== 'string' || position.id.trim() === '') {
      continue;
    }

    // Keep ALL fields to maintain consistency with form state
    // This prevents sync issues between local state and form state
    const sanitizedPosition: PositionEntry = {
      id: position.id.trim(),
      label: typeof position.label === 'string' ? position.label.trim() : '',
      latitude: typeof position.latitude === 'string' ? position.latitude.trim() : '',
      longitude: typeof position.longitude === 'string' ? position.longitude.trim() : '',
    };

    // Only include status if it's explicitly set
    if (typeof position.status === 'boolean') {
      sanitizedPosition.status = position.status;
    }

    result.push(sanitizedPosition);
  }

  return result.length > 0 ? result : undefined;
}

interface StatusRangeEntry {
  id?: string;
  startDate?: string;
  endDate?: string;
  statusDuring?: 'red' | 'yellow' | 'green';
  fallbackAfter?: 'red' | 'yellow' | 'green';
}

interface StatusEntry {
  mode?: 'manual' | 'scheduled';
  manualStatus?: 'red' | 'yellow' | 'green';
  ranges?: StatusRangeEntry[];
  defaultStatus?: 'red' | 'yellow' | 'green';
}

function sanitizeStatusValue(val: unknown): StatusEntry | undefined {
  if (!val || typeof val !== 'object') return undefined;

  const status = val as StatusEntry;

  // mode and defaultStatus are required
  if (!status.mode || !status.defaultStatus) {
    return undefined;
  }

  const result: StatusEntry = {
    mode: status.mode,
    defaultStatus: status.defaultStatus,
  };

  if (status.mode === 'manual' && status.manualStatus) {
    result.manualStatus = status.manualStatus;
  }

  if (status.mode === 'scheduled' && status.ranges && Array.isArray(status.ranges)) {
    const sanitizedRanges: StatusRangeEntry[] = [];

    for (const range of status.ranges) {
      if (!range || typeof range !== 'object') continue;
      if (!range.id || !range.startDate || !range.endDate || !range.statusDuring) continue;

      const sanitizedRange: StatusRangeEntry = {
        id: range.id,
        startDate: range.startDate,
        endDate: range.endDate,
        statusDuring: range.statusDuring,
      };

      if (range.fallbackAfter) {
        sanitizedRange.fallbackAfter = range.fallbackAfter;
      }

      sanitizedRanges.push(sanitizedRange);
    }

    if (sanitizedRanges.length > 0) {
      result.ranges = sanitizedRanges;
    }
  }

  return result;
}

/**
 * Sanitizes metadata values to match the expected types in the schema.
 * Handles type conversions like string->number, string->boolean, string->date.
 * This is needed because form inputs often return strings even for number fields.
 */
export function sanitizeMetadataValues(
  values: Record<string, unknown> | undefined,
  schema: JsonSchemaType | undefined
): Record<string, unknown> {
  if (!schema?.properties) return {};

  const safeValues = values ?? {};

  const result: Record<string, unknown> = {};

  for (const key of Object.keys(schema.properties)) {
    const def = schema.properties[key] as {
      type?: string;
      format?: string;
    };
    const val = safeValues[key];
    const expectedType = def.format === 'date-time' ? 'date' : def.type;

    if (expectedType === 'contact') {
      const sanitizedContact = sanitizeContactValue(val);
      if (sanitizedContact !== undefined) {
        result[key] = sanitizedContact;
      }
      continue;
    }

    if (expectedType === 'positions') {
      const sanitizedPositions = sanitizePositionsValue(val);
      if (sanitizedPositions !== undefined) {
        result[key] = sanitizedPositions;
      }
      continue;
    }

    if (expectedType === 'status') {
      const sanitizedStatus = sanitizeStatusValue(val);
      if (sanitizedStatus !== undefined) {
        result[key] = sanitizedStatus;
      }
      continue;
    }

    if (expectedType === 'number') {
      if (typeof val === 'number') {
        result[key] = val;
      } else if (typeof val === 'string') {
        const num = val.trim() === '' ? NaN : Number(val);
        if (!Number.isNaN(num)) {
          result[key] = num;
        }
        // If NaN, leave unset; required will surface via validation
      }
      // If neither number nor string, leave unset
      continue;
    }

    if (expectedType === 'boolean') {
      if (val === undefined || val === null || val === '') {
        result[key] = false;
        continue;
      }

      if (typeof val === 'boolean') {
        result[key] = val;
      } else if (typeof val === 'string') {
        const lowered = val.trim().toLowerCase();
        if (lowered === 'true' || lowered === '1') {
          result[key] = true;
        } else if (lowered === 'false' || lowered === '0') {
          result[key] = false;
        } else {
          result[key] = false;
        }
      } else if (typeof val === 'number') {
        result[key] = val !== 0;
      } else {
        result[key] = false;
      }
      continue;
    }

    if (expectedType === 'date') {
      if (val instanceof Date && !Number.isNaN(val.getTime())) {
        result[key] = val;
      } else if (typeof val === 'string') {
        const d = new Date(val);
        if (!Number.isNaN(d.getTime())) {
          result[key] = d;
        }
        // If invalid date, leave unset; required will surface via validation
      }
      // If neither Date nor string, leave unset
      continue;
    }

    // Default to string type
    if (typeof val === 'string') {
      result[key] = val;
    } else if (val != null) {
      // Convert to string if possible
      result[key] = String(val);
    }
  }

  return result;
}
